import React, { useContext, useRef, useMemo } from 'react';
import { useAsync } from 'react-commons';
import { Column, TableInstance } from 'react-table';
import { Button } from 'react-bootstrap';
import ConfigContext from '../../../contexts/ConfigContext';
import ErrorBlock from '../../common/ErrorBlock';
import Spinner from '../../common/Spinner';
import { getTemplates } from '../../../api/amTemplateApi';
import { AmTemplateType } from '../../../api/amTemplateApiTypes';
import DataTable from '../../common/datatables/DataTable';
import { generateCSV } from '../../common/listUtils';
import { DEFAULT_ADMIN_INITIAL_STATE } from '../../../utils/Constants';
import useStateWithSessionStorage from '../../../hooks/useStateWithSessionStorage';
import {
    convertEmptyStringToDashes,
    formatBooleanAsYesNo,
    formatExportText,
} from '../../../utils/displayUtils';

interface TemplateMaintenanceItemType {
    operatingUnit?: string | null;
    code: string;
    templateType: string;
    description: string;
    hidden: string;
    completionDays: number;
    showBudgetAmount: string;
    showInternalCostAmount: string;
    showRevenueAmount: string;
    showDevCode: string;
    autoApprove: string;
}

const TemplateMaintenance = () => {
    const config = useContext(ConfigContext);

    const [initialState, setInitialState] = useStateWithSessionStorage(
        'templateMaintenanceInitialState',
        JSON.stringify(DEFAULT_ADMIN_INITIAL_STATE),
    );

    const defaultState: Array<AmTemplateType> = [];
    const { value, error, loading } = useAsync<Array<AmTemplateType>>(
        () => getTemplates(config),
        defaultState,
    );

    const tableInstanceRef = useRef<TableInstance<TemplateMaintenanceItemType> | null>(null);

    if (error) {
        return typeof error === 'object' ? (
            <ErrorBlock errorMessage="Network Error!" />
        ) : (
            <ErrorBlock errorMessage={error} />
        );
    }

    const templates: TemplateMaintenanceItemType[] = value.map((template) => ({
        operatingUnit: template.operatingUnit,
        code: template.code,
        templateType: template.templateType,
        description: template.description,
        hidden: template.hidden ? 'Hidden' : 'Visible',
        completionDays: template.completionDays,
        showBudgetAmount: template.showBudgetAmount ? 'Show' : 'Hide',
        showInternalCostAmount: template.showInternalCostAmount ? 'Show' : 'Hide',
        showRevenueAmount: template.showRevenueAmount ? 'Show' : 'Hide',
        showDevCode: template.showDevCode ? 'Show' : 'Hide',
        autoApprove: formatBooleanAsYesNo(template.autoApprove),
    }));

    const columns = [
        {
            Header: 'Operating Unit',
            accessor: 'operatingUnit',
            width: 100,
        },
        {
            Header: 'Template Code',
            accessor: 'code',
            width: 200,
        },
        {
            Header: 'Template Type',
            accessor: 'templateType',
            Cell: (cellProps: { value: string }) =>
                useMemo(() => <span>{convertEmptyStringToDashes(cellProps.value)}</span>, []),
            width: 250,
        },
        {
            Header: 'Description',
            accessor: 'description',
            width: null,
        },
        {
            Header: 'Hidden',
            accessor: 'hidden',
            width: 100,
        },
        {
            Header: 'Completion Days',
            accessor: 'completionDays',
            width: 100,
        },
        {
            Header: 'Budget',
            accessor: 'showBudgetAmount',
            width: 100,
        },
        {
            Header: 'Internal Cost',
            accessor: 'showInternalCostAmount',
            width: 100,
        },
        {
            Header: 'Expected Revenue',
            accessor: 'showRevenueAmount',
            width: 100,
        },
        {
            Header: 'NCL Dev Code',
            accessor: 'showDevCode',
            width: 100,
        },
        {
            Header: 'Auto Approve',
            accessor: 'autoApprove',
            width: 100,
        },
    ];

    return (
        <div className="container-fluid mt-3">
            <div className="alert alert-info">
                Please manage templates in Oracle EBS as this feature is not supported through this
                interface.
            </div>
            <div className="row row-page-header">
                <div className="col-12 d-flex align-items-center mb-2">
                    <h3 className="mr-auto text-weight-normal mb-0">Template</h3>

                    <div className="pull-right">
                        <Button
                            variant="outline-primary"
                            disabled={loading || templates.length <= 0}
                            onClick={() => {
                                if (tableInstanceRef.current) {
                                    const list = tableInstanceRef.current.rows
                                        .map((row) => row.original)
                                        .map((template) => ({
                                            'Operating Unit': template.operatingUnit,
                                            'Template Code': template.code,
                                            'Template Type': formatExportText(
                                                template.templateType,
                                            ),
                                            Description: formatExportText(template.description),
                                            Hidden: template.hidden,
                                            'Completion Days': template.completionDays,
                                            Budget: template.showBudgetAmount,
                                            'Internal Cost': template.showInternalCostAmount,
                                            'Expected Revenue': template.showRevenueAmount,
                                            'NCL Dev Code': template.showDevCode,
                                            'Auto Approve': template.autoApprove,
                                        }));
                                    generateCSV(list, 'Templates.csv');
                                }
                            }}
                        >
                            <i className="fas fa-file-export" /> Export CSV
                        </Button>
                    </div>
                </div>
            </div>

            <div className="card border-top">
                <div className="card-body">
                    {!loading ? (
                        <DataTable<TemplateMaintenanceItemType>
                            columns={columns as Column<TemplateMaintenanceItemType>[]}
                            data={templates}
                            initialState={JSON.parse(initialState)}
                            setInitialState={setInitialState}
                            noGlobalFilter
                            getInstanceCallback={(tableInstance) => {
                                if (tableInstanceRef) {
                                    tableInstanceRef.current = tableInstance;
                                }
                            }}
                        />
                    ) : (
                        <Spinner />
                    )}
                </div>
            </div>
        </div>
    );
};

export default TemplateMaintenance;
